import { useEffect, useId, useState } from 'react';
import type { Block, Lesson } from '../../content/types';
import { useApp } from '../../state/store';
import { InteractiveMath } from '../InteractiveMath';
import { parseMarkup } from '../Markup';
import { Widget } from '../widgets/Widget';
import { LabPanel } from './LabPanel';

export function Blocks({ blocks, lesson }: { blocks: Block[]; lesson: Lesson }) {
  return (
    <>
      {blocks.map((b, i) => (
        <BlockView key={i} block={b} lesson={lesson} />
      ))}
    </>
  );
}

function BlockView({ block: b, lesson }: { block: Block; lesson: Lesson }) {
  switch (b.type) {
    case 'p':
      return <p>{parseMarkup(b.text)}</p>;
    case 'h':
      return <h3>{b.text}</h3>;
    case 'list': {
      const Tag = b.ordered ? 'ol' : 'ul';
      return (
        <Tag>
          {b.items.map((it, i) => (
            <li key={i}>{parseMarkup(it, `li${i}`)}</li>
          ))}
        </Tag>
      );
    }
    case 'callout':
      return (
        <aside className={`callout ${b.tone}`}>
          {b.title && <p className="callout-title">{b.title}</p>}
          <p>{parseMarkup(b.text)}</p>
        </aside>
      );
    case 'math':
      return <MathBlock tex={b.tex} caption={b.caption} aria={b.aria} />;
    case 'table':
      return (
        <div className="table-scroll">
          <table className="content-table">
            <caption>{b.caption}</caption>
            <thead>
              <tr>
                {b.headers.map((h) => (
                  <th key={h} scope="col">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {b.rows.map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) => (j === 0 ? <th key={j} scope="row">{parseMarkup(cell, `c${i}${j}`)}</th> : <td key={j}>{parseMarkup(cell, `c${i}${j}`)}</td>))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'widget':
      return <Widget id={b.widget} />;
    case 'predict':
      return <PredictBlock id={b.id} prompt={b.prompt} options={b.options} reveal={b.reveal} lesson={lesson} />;
    case 'reflect':
      return <ReflectBlock id={b.id} prompt={b.prompt} />;
    case 'optional':
      return (
        <details className="optional">
          <summary>{b.title}</summary>
          <Blocks blocks={b.blocks} lesson={lesson} />
        </details>
      );
    case 'lab':
      return <LabPanel lesson={lesson} />;
  }
}

export function MathBlock({ tex, caption, aria }: { tex: string; caption?: string; aria?: string }) {
  return (
    <figure className="math-figure">
      {caption && <figcaption>{parseMarkup(caption)}</figcaption>}
      <InteractiveMath tex={tex} aria={aria} />
    </figure>
  );
}

function PredictBlock({ id, prompt, options, reveal, lesson }: { id: string; prompt: string; options: { id: string; text: string }[]; reveal: string; lesson: Lesson }) {
  const committed = useApp((s) => s.predictions[id]);
  const setPrediction = useApp((s) => s.setPrediction);
  const hasEvidence = useApp((s) => lesson.lab.steps.some((st) => s.progress.evidence[st.id]));
  const [choice, setChoice] = useState<string | null>(committed ?? null);
  const name = useId();
  return (
    <fieldset className="predict">
      <legend>Your prediction</legend>
      <p>{parseMarkup(prompt)}</p>
      {options.map((o) => (
        <label key={o.id} className="option">
          <input type="radio" name={name} value={o.id} checked={choice === o.id} disabled={!!committed} onChange={() => setChoice(o.id)} />
          <span>{parseMarkup(o.text, o.id)}</span>
        </label>
      ))}
      {!committed ? (
        <button type="button" className="btn" disabled={!choice} onClick={() => choice && setPrediction(id, choice)}>
          Commit prediction
        </button>
      ) : hasEvidence ? (
        <div className="reveal">
          <p className="small">
            You predicted: <strong>{parseMarkup(options.find((o) => o.id === committed)?.text ?? '', 'pc')}</strong>
          </p>
          <p>{parseMarkup(reveal, 'rv')}</p>
        </div>
      ) : (
        <p className="small muted">Prediction saved. Run the experiment below, then come back to compare.</p>
      )}
    </fieldset>
  );
}

function ReflectBlock({ id, prompt }: { id: string; prompt: string }) {
  const saved = useApp((s) => s.reflections[id] ?? '');
  const setReflection = useApp((s) => s.setReflection);
  const [text, setText] = useState(saved);
  const fieldId = useId();
  useEffect(() => {
    const h = setTimeout(() => text !== saved && setReflection(id, text), 600);
    return () => clearTimeout(h);
  }, [text, saved, id, setReflection]);
  return (
    <div className="reflect">
      <label htmlFor={fieldId}>{parseMarkup(prompt)}</label>
      <textarea id={fieldId} rows={3} value={text} onChange={(e) => setText(e.target.value)} />
    </div>
  );
}
